// useGroceryList.js — Grocery list generation logic extracted from MealPlan
import { useState, useCallback, useRef, useEffect } from "react";
import { getUserCountry } from "../../../services/apiService";
import { processGroceryListAI } from "../../../utils/aiGroceryFallback";
import { logTestFlow, getStoredPrompt } from "../../../utils/testFlowLogger";

/**
 * Manages grocery list generation, country detection, and related effects.
 *
 * @param {Object} deps
 * @param {number} deps.numberOfPeople
 * @param {string} deps.budget
 * @param {Function} deps.setMealPlan - From MealPlanContext — updated with scaled ingredient qtys
 * @param {Function} deps.setProductList - From MealPlanContext
 * @param {Function} deps.setTotalCost - From MealPlanContext
 * @param {Function} deps.setLeftovers - From MealPlanContext
 * @param {Function} deps.updateStreamProgress - From MealPlanContext
 * @param {Function} deps.addReasoningStep
 * @param {React.MutableRefObject<boolean>} deps.isMountedRef
 * @param {React.MutableRefObject<Map>} [deps.prematchCacheRef] - Shared per-recipe
 *   Pass 1 match cache. When provided, final grocery generation reuses these
 *   matches and only runs Pass 2 + AI fallback for ingredients NOT in the cache.
 * @returns {Object}
 */
export function useGroceryList({
  numberOfPeople,
  budget,
  setMealPlan,
  setProductList,
  setTotalCost,
  setAdjustedTotalCost,
  setLeftovers,
  updateStreamProgress,
  addReasoningStep,
  isMountedRef,
  prematchCacheRef,
}) {
  const [groceryListLoading, setGroceryListLoading] = useState(false);
  const [groceryOperationInProgress, setGroceryOperationInProgress] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const productsCount = useRef(0);
  const hasGeneratedForCountry = useRef(false);
  const hasRegeneratedGroceryList = useRef(false);
  // In-flight guard: prevents concurrent grocery runs (multiple call sites:
  // main loader, country effect, restore effect) which would otherwise produce
  // divergent setTotalCost writes and duplicate cost-summary log lines.
  const inFlightRef = useRef(false);

  // Fetch country on mount
  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const country = await getUserCountry();
        setUserCountry(country);
      } catch (error) {
        console.error("Error fetching country:", error);
      }
    };
    fetchCountry();
  }, []);

  /**
   * Generate grocery list from recipes
   * @param {Array} recipes
   * @returns {Promise<Object|undefined>} result with products, totalCost, leftovers
   */
  const generateGroceryList = useCallback(
    async (recipes, options = {}) => {
      const { onTypesenseMatched } = options;
      if (!recipes || recipes.length === 0) return;
      if (!isMountedRef.current) {
        console.log('[useGroceryList] Skipping - component unmounted');
        return;
      }
      if (inFlightRef.current) {
        console.log('[useGroceryList] Skipping - generation already in flight');
        return;
      }
      inFlightRef.current = true;

      setGroceryListLoading(true);
      addReasoningStep(`Getting your grocery list ready...`);

      try {
        let effectiveCountry = userCountry;

        if (!effectiveCountry) {
          addReasoningStep(`Checking your shopping country...`);
          effectiveCountry = await getUserCountry();
          if (!isMountedRef.current) {
            console.log('[useGroceryList] Aborting - unmounted during country detection');
            return;
          }
          setUserCountry(effectiveCountry);
        }

        addReasoningStep(`Building your grocery list for ${effectiveCountry}...`);

        // Hand the per-recipe prematch cache to the final pipeline. Items present
        // in the cache (matched by Pass 1 during streaming) skip Pass 1+2+AI in
        // matchIngredientsToProductsMetric — only true unresolved ingredients are
        // resolved via Pass 2 + AI fallback at this stage. Drives the dedupe that
        // keeps total AI calls under backend rate limits.
        const prematchCache = prematchCacheRef?.current && prematchCacheRef.current.size > 0
          ? prematchCacheRef.current
          : null;
        if (prematchCache) {
          console.log(`[useGroceryList] Using prematch cache with ${prematchCache.size} entries`);
        }

        const result = await processGroceryListAI({
          mealPlan: recipes,
          country: effectiveCountry,
          numberOfPeople,
          budget,
          useEnhancedMatching: true,
          prematchCache,
          onProgress: (progress) => {
            if (!isMountedRef.current) return;
            if (progress.step === 'typesense_matched' && progress.matchedProducts && onTypesenseMatched) {
              onTypesenseMatched(progress.matchedProducts);
            }
            addReasoningStep(
              `Grocery list: ${progress.message} (${progress.progress || 0}%)`
            );
            setProcessingStatus({
              step: progress.step,
              progress: progress.progress || 0,
              message: progress.message,
            });
          },
        });

        if (!isMountedRef.current) {
          console.log('[useGroceryList] Ignoring results - component unmounted');
          return;
        }

        if (result.error) throw new Error(result.error);

        // Merge scaled ingredient quantities into current meal plan state
        // WITHOUT overwriting recipe details, images, or updated titles
        // that progressive processor may have already applied.
        if (result.updatedMealPlan && setMealPlan) {
          setMealPlan(prev => {
            if (!prev || prev.length === 0) return result.updatedMealPlan;
            return prev.map(existingRecipe => {
              // Find matching recipe in updatedMealPlan by title (original or current)
              const scaled = result.updatedMealPlan.find(r =>
                r.title === existingRecipe.title ||
                r.title === existingRecipe.originalTitle ||
                (existingRecipe.title && r.title && r.title === existingRecipe.title)
              );
              if (!scaled) return existingRecipe;
              // Only merge ingredient quantities — preserve everything else
              return {
                ...existingRecipe,
                ingredients: scaled.ingredients,
              };
            });
          });
        }

        setProductList(result.products || []);
        setTotalCost(result.totalCost || "€0.00");
        setAdjustedTotalCost(null);
        setLeftovers(result.leftovers || []);

        // TESTFLOW logging
        console.log('[useGroceryList] About to call logTestFlow, recipes:', recipes?.length, 'products:', result.products?.length);
        logTestFlow({
          prompt: getStoredPrompt(),
          recipes: recipes,
          groceryList: result.products || [],
          leftovers: result.leftovers || [],
        });

        const totalProducts = Array.isArray(result.products)
          ? result.products.reduce(
              (sum, category) =>
                sum + (Array.isArray(category.items) ? category.items.length : 0),
              0
            )
          : 0;
        updateStreamProgress({ products: totalProducts });
        productsCount.current = totalProducts;

        return result;
      } catch (error) {
        if (!isMountedRef.current) {
          console.log('[useGroceryList] Ignoring error - component unmounted');
          return;
        }
        addReasoningStep(`Error in grocery processing: ${error.message}`);
        setProductList([]);
        setLeftovers([]);
      } finally {
        if (isMountedRef.current) {
          setGroceryListLoading(false);
        }
        // Consume-and-clear: the prematch cache is valid for exactly ONE final
        // grocery generation cycle (the one immediately following the stream
        // that populated it). Any subsequent grocery regen — admin product
        // replace + manual re-run, recipe replacement, restore-after-reload —
        // must hit Typesense fresh so updates (e.g. admin keyword swaps) take
        // effect. Without this clear, stale Pass 1 hits from the previous
        // generation would short-circuit the new matching and surface the OLD
        // wrong product.
        if (prematchCacheRef?.current) {
          prematchCacheRef.current.clear();
        }
        inFlightRef.current = false;
      }
    },
    [numberOfPeople, budget, userCountry, setMealPlan, setProductList, setTotalCost, setAdjustedTotalCost, setLeftovers, addReasoningStep, updateStreamProgress, isMountedRef, prematchCacheRef]
  );

  /**
   * Setup country-driven grocery generation effect.
   * Call this from the component to wire up the country → generate effect.
   * @param {Array} mealPlan
   * @param {Array} productList
   * @param {boolean} restoredFromLocalStorage
   */
  const setupCountryEffect = useCallback(
    (mealPlan, productList, restoredFromLocalStorage) => {
      // This is called from the effect body in MealPlan
      if (restoredFromLocalStorage) return;
      if (productList && productList.length > 0) return;
      if (hasGeneratedForCountry.current) return;
      if (userCountry && mealPlan.length > 0) {
        hasGeneratedForCountry.current = true;
        generateGroceryList(mealPlan);
      }
    },
    [userCountry, generateGroceryList]
  );

  /**
   * Regenerate grocery list after restore if missing.
   * @param {boolean} restoredFromLocalStorage
   * @param {Array} mealPlan
   * @param {Array} productList
   */
  const regenerateAfterRestore = useCallback(
    (restoredFromLocalStorage, mealPlan, productList) => {
      if (
        restoredFromLocalStorage &&
        userCountry &&
        mealPlan.length > 0 &&
        (!productList || productList.length === 0)
      ) {
        if (hasRegeneratedGroceryList.current) return;
        hasRegeneratedGroceryList.current = true;
        console.log("[useGroceryList] Grocery list missing after restore, regenerating for", userCountry);
        setGroceryListLoading(true);
        generateGroceryList(mealPlan);
      }
    },
    [userCountry, generateGroceryList]
  );

  return {
    groceryListLoading,
    setGroceryListLoading,
    groceryOperationInProgress,
    setGroceryOperationInProgress,
    processingStatus,
    userCountry,
    generateGroceryList,
    setupCountryEffect,
    regenerateAfterRestore,
  };
}
